// Core packages
import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// Third party packages
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

// Custom packages
import { MatDialog, Sort } from '@angular/material';
import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import moment from 'moment';
import { Color, Label, MultiDataSet, PluginServiceGlobalRegistrationAndOptions } from 'ng2-charts';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { PropertiesService } from 'src/app/modules/properties/properties.service';
import { StatsService } from 'src/app/modules/stats/stats.service';
import { UsersService } from 'src/app/modules/users/users.service';
import { InfoDialogComponent } from 'src/app/shared/components/dialogs/info-dialog/info-dialog.component';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { PropertiesInStructureListDialogComponent } from '../../components/properties-in-structure-list-dialog/properties-in-structure-list-dialog.component';
@Component({
  selector: 'app-stats-list',
  templateUrl: './stats-list.page.html',
  styleUrls: ['./stats-list.page.scss']
})
export class StatsListComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  lang: string;
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRoleType: string;
  activeRoute: string;
  data: any;
  sortedSubscriptionsData = [];
  sortedPricesData = [];

  // BEGIN - turnoverDoughnut chart
  doughnutChartLabels: Label[] = [];
  doughnutChartData: MultiDataSet = [];
  doughnutChartType: ChartType = 'doughnut';
  doughnutChartLegend = true;
  doughnutChartColors: Color[] = [];
  doughnutChartOptions: ChartOptions = {
    maintainAspectRatio: true,
    responsive: true,
    // title: {
    //   display: false,
    //   text: '',
    //   position: 'top'
    // },
    legend: {
      display: true,
      position: 'right',
      fullWidth: true,
      reverse: false,
      labels: {
        fontColor: '#333333',
        fontSize: 15,
        fontFamily: 'Arial',

      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          // get the data label and data value to display
          let dataLabel = data.labels[tooltipItem.index] as string;
          // convert the data value to local string so it uses a comma seperated number
          const value = ` (${data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].toLocaleString()}`;

          // Put suffix on label
          dataLabel += `${value}%)`;

          return dataLabel;
        }
      },
    }
  };
  doughnutChartPlugins: PluginServiceGlobalRegistrationAndOptions[] = [{
    afterDraw: (chart: Chart | any) => {

    },
    beforeDraw: (chart: Chart | any) => {
      // Get ctx from string
      const ctx = chart.ctx;
    },
  },];
  // END - turnoverDoughnut chart

  // BEGIN - companiesLine chart
  lineChartLabels: Label[] = [];
  lineChartData: ChartDataSets[] = [];
  lineChartType: ChartType = 'line';
  lineChartLegend = true;
  lineChartColors: Color[] = [];
  lineChartOptions: ChartOptions = {
    title: {
      display: false,
      text: '',
      position: 'top'
    },
    responsive: true,
    legend: {
      display: true,
      position: 'top',
      fullWidth: true,
      reverse: false,
      labels: {
        fontColor: '#333333',
        fontSize: 15,
        fontFamily: 'Arial',
      }
    },
    scales: {
      xAxes: [{
        gridLines: {
          color: 'rgba(0, 0, 0, 0.05)',
        }
      }],
      yAxes: [{
        gridLines: {
          color: 'rgba(0, 0, 0, 0.05)',
        }
      }]
    }
  };
  lineChartPlugins: PluginServiceGlobalRegistrationAndOptions[] = [];
  // END - companiesLine chart

  // Counters
  usersCount: number;
  companiesCount: number;
  propertiesCount: number;
  subscriptionsCount: number;
  accessLogsCount: number;
  deadlinesCount: number;

  totalDocs = {
    totMandatory: 0,
    totInsert: 0,
    totNotInsert: 0,
    totOptional: 0
  };
  progressBarVal = 0;
  progressBarValStyle = { 'width': '0px' };

  subscriptionsRevenue = {
    full: 0,
    fullPerc: 0,
    free: 0,
    freePerc: 0,
    test: 0,
    testPerc: 0,
    contratti: 0,
    contrattiPerc: 0,
    checklist: 0,
    checklistPerc: 0,
    pacchettofisso: 0,
    pacchettofissoPerc: 0,
    nfcAccess: 0,
    nfcAccessPerc: 0,
    qrsicurezza: 0,
    qrsicurezzaPerc: 0
  };
  totalSubscriptionsRevenue = 0;
  today = new Date();
  structures: any[];
  structuresToDisplay: string[];

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,
    private statsService: StatsService,
    private helperService: HelperService,
    private toastrService: ToastrService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private router: Router,
    private cookieService: CookieService,
    private configService: ConfigService,
    private usersService: UsersService,
    private propertiesService: PropertiesService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('stats.list.pageTitle'));

    // Get current lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;

    this.structuresToDisplay = this.configService.structuresToDisplay;

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => {
        this.loggedUser = res
        if (this.loggedUser) {
          // current role
          const currentRole = this.loggedUser.userRoles.find((obj: any) => {
            return (
              obj._id.toString() ===
              this.authService.loggedUserRequestedRole$.getValue().toString()
            );
          });

          this.loggedUserRoleType = currentRole.type;

          if ((this.loggedUserRoleType === "admin"
            || this.loggedUserRoleType === "operator"
            || this.loggedUserRoleType === "master"
            || this.loggedUserRoleType === "collaborator")
          ) {
            // Get statistics
            this.subscriptions.push(
              this.statsService.get().subscribe(
                res => {
                  if (res.status && res.stats) {
                    this.data = res.stats;
                    this.sortedSubscriptionsData = JSON.parse(JSON.stringify(this.data.admins));
                    this.sortedPricesData = JSON.parse(JSON.stringify(this.data.admins));
                    this.calcSubscriptionsData();
                    this.buildTabsData();
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              ));
          } else {
            // Role not authorized, reject and redirect home
            this.toastrService.error('common.errors.not_authorized')
            this.router.navigate([this.lang, "app"]);
          }
        }
      })
    );

    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Calculate values to display
   *
   * @since 1.0.0
   */
  calcSubscriptionsData(): void {
    if (!this.data) {
      this.toastrService.error("common.errors.no_data");
    }
    let fullTot = 0;
    let freeTot = 0;
    let testTot = 0;
    let contrattiTot = 0;
    let checklistTot = 0;
    let pacchettofissoTot = 0;
    let nfcAccessTot = 0;
    let qrsicurezzaTot = 0;
    this.data.admins.forEach(el => {
      if (el.full) {
        fullTot += el.full;
      }
      if (el.free) {
        freeTot += el.free;
      }
      if (el.test) {
        testTot += el.test;
      }
      if (el.contratti) {
        contrattiTot += el.contratti;
      }
      if (el.checklist) {
        checklistTot += el.checklist;
      }
      if (el.pacchettofisso) {
        pacchettofissoTot += el.pacchettofisso;
      }
      if (el.nfcAccess) {
        nfcAccessTot += el.nfcAccess;
      }
      if (el.qrsicurezza) {
        qrsicurezzaTot += el.qrsicurezza;
      }
    });
    const subscriptionsData = {
      full: fullTot,
      free: freeTot,
      test: testTot,
      contratti: contrattiTot,
      checklist: checklistTot,
      pacchettofisso: pacchettofissoTot,
      nfcAccess: nfcAccessTot,
      qrsicurezza: qrsicurezzaTot,
    }

    if (subscriptionsData) {
      this.subscriptionsRevenue.full = subscriptionsData.full;
      this.subscriptionsRevenue.free = subscriptionsData.free;
      this.subscriptionsRevenue.test = subscriptionsData.test;
      this.subscriptionsRevenue.contratti = subscriptionsData.contratti;
      this.subscriptionsRevenue.checklist = subscriptionsData.checklist;
      this.subscriptionsRevenue.pacchettofisso = subscriptionsData.pacchettofisso;
      this.subscriptionsRevenue.nfcAccess = subscriptionsData.nfcAccess;
      this.subscriptionsRevenue.qrsicurezza = subscriptionsData.qrsicurezza;

      this.totalSubscriptionsRevenue = this.subscriptionsRevenue.full +
        this.subscriptionsRevenue.free +
        this.subscriptionsRevenue.test +
        this.subscriptionsRevenue.contratti +
        this.subscriptionsRevenue.checklist +
        this.subscriptionsRevenue.pacchettofisso +
        this.subscriptionsRevenue.nfcAccess +
        this.subscriptionsRevenue.qrsicurezza;

      this.subscriptionsRevenue.fullPerc = ((this.subscriptionsRevenue.full / this.totalSubscriptionsRevenue) * 100);
      this.subscriptionsRevenue.freePerc = ((this.subscriptionsRevenue.free / this.totalSubscriptionsRevenue) * 100);
      this.subscriptionsRevenue.testPerc = ((this.subscriptionsRevenue.test / this.totalSubscriptionsRevenue) * 100);
      this.subscriptionsRevenue.contrattiPerc = ((this.subscriptionsRevenue.contratti / this.totalSubscriptionsRevenue) * 100);
      this.subscriptionsRevenue.checklistPerc = ((this.subscriptionsRevenue.checklist / this.totalSubscriptionsRevenue) * 100);
      this.subscriptionsRevenue.pacchettofissoPerc = ((this.subscriptionsRevenue.pacchettofisso / this.totalSubscriptionsRevenue) * 100);
      this.subscriptionsRevenue.nfcAccessPerc = ((this.subscriptionsRevenue.nfcAccess / this.totalSubscriptionsRevenue) * 100);
      this.subscriptionsRevenue.qrsicurezzaPerc = ((this.subscriptionsRevenue.qrsicurezza / this.totalSubscriptionsRevenue) * 100);

      this.subscriptionsRevenue.fullPerc = Number.parseFloat(this.subscriptionsRevenue.fullPerc.toFixed(2));
      this.subscriptionsRevenue.freePerc = Number.parseFloat(this.subscriptionsRevenue.freePerc.toFixed(2));
      this.subscriptionsRevenue.testPerc = Number.parseFloat(this.subscriptionsRevenue.testPerc.toFixed(2));
      this.subscriptionsRevenue.contrattiPerc = Number.parseFloat(this.subscriptionsRevenue.contrattiPerc.toFixed(2));
      this.subscriptionsRevenue.checklistPerc = Number.parseFloat(this.subscriptionsRevenue.checklistPerc.toFixed(2));
      this.subscriptionsRevenue.pacchettofissoPerc = Number.parseFloat(this.subscriptionsRevenue.pacchettofissoPerc.toFixed(2));
      this.subscriptionsRevenue.nfcAccessPerc = Number.parseFloat(this.subscriptionsRevenue.nfcAccessPerc.toFixed(2));
      this.subscriptionsRevenue.qrsicurezzaPerc = Number.parseFloat(this.subscriptionsRevenue.qrsicurezzaPerc.toFixed(2));
    }
  }

  /**
   * Build appropriate content for dashboard tabs depending on current logged role type
   *
   * @since 1.0.0
   */
  buildTabsData(): void {
    if (this.loggedUserRoleType === "shared") {
      const lang = this.translateService.currentLang || this.translateService.defaultLang;
      this.router.navigate([lang, 'app', 'properties', 'my-property']);
      return;
    }

    if (this.loggedUserRoleType === "temporary") {
      const lang = this.translateService.currentLang || this.translateService.defaultLang;
      this.router.navigate([lang, 'app', 'properties', 'list']);
      return;
    }

    // Reset chart's data
    this.doughnutChartLabels = [];
    this.doughnutChartColors = [];
    this.doughnutChartData = [];
    this.lineChartLabels = [];
    this.lineChartData = [];
    this.lineChartColors = [];

    // Doughnut Chart
    if (this.subscriptionsRevenue.full && this.subscriptionsRevenue.full >= 0) {
      this.doughnutChartLabels
        .push(`${this.translateService.instant('home.full')}: ${this.subscriptionsRevenue.full.toFixed(2)} €`);
    }
    if (this.subscriptionsRevenue.free && this.subscriptionsRevenue.free >= 0) {
      this.doughnutChartLabels
        .push(`${this.translateService.instant('home.free')}: ${this.subscriptionsRevenue.free.toFixed(2)} €`);
    }
    if (this.subscriptionsRevenue.test && this.subscriptionsRevenue.test >= 0) {
      this.doughnutChartLabels
        .push(`${this.translateService.instant('home.test')}: ${this.subscriptionsRevenue.test.toFixed(2)} €`);
    }
    if (this.subscriptionsRevenue.contratti && this.subscriptionsRevenue.contratti >= 0) {
      this.doughnutChartLabels
        .push(`${this.translateService.instant('home.contracts')}: ${this.subscriptionsRevenue.contratti.toFixed(2)} €`);
    }
    if (this.subscriptionsRevenue.checklist && this.subscriptionsRevenue.checklist >= 0) {
      this.doughnutChartLabels
        .push(`${this.translateService.instant('home.checklist')}: ${this.subscriptionsRevenue.checklist.toFixed(2)} €`);
    }
    // if (this.subscriptionsRevenue.pacchettofisso && this.subscriptionsRevenue.pacchettofisso > 0) {
    //   this.doughnutChartLabels
    //     .push(`${this.translateService.instant('home.fixedPackage')}: ${this.subscriptionsRevenue.pacchettofisso.toFixed(2)} €`);
    // }
    if (this.subscriptionsRevenue.qrsicurezza && this.subscriptionsRevenue.qrsicurezza >= 0) {
      this.doughnutChartLabels
        .push(`${this.translateService.instant('home.qrSecurity')}: ${this.subscriptionsRevenue.qrsicurezza.toFixed(2)} €`);
    }
    if (this.subscriptionsRevenue.nfcAccess && this.subscriptionsRevenue.nfcAccess >= 0) {
      this.doughnutChartLabels
        .push(`${this.translateService.instant('home.nfcAccess')}: ${this.subscriptionsRevenue.nfcAccess.toFixed(2)} €`);
    }

    this.doughnutChartColors.push(
      {
        backgroundColor: [
          '#EF5350',
          '#EC407A',
          '#AB47BC',
          '#7E57C2',
          '#5C6BC0',
          '#42A5F5',
          // '#29B6F6',
          '#26C6DA',
        ],
      },
    );
    const chartValues = [];
    if (this.subscriptionsRevenue.fullPerc && this.subscriptionsRevenue.fullPerc > 0) {
      chartValues.push(this.subscriptionsRevenue.fullPerc);
    }
    if (this.subscriptionsRevenue.freePerc && this.subscriptionsRevenue.freePerc > 0) {
      chartValues.push(this.subscriptionsRevenue.freePerc);
    }
    if (this.subscriptionsRevenue.testPerc && this.subscriptionsRevenue.testPerc > 0) {
      chartValues.push(this.subscriptionsRevenue.testPerc);
    }
    if (this.subscriptionsRevenue.contrattiPerc && this.subscriptionsRevenue.contrattiPerc > 0) {
      chartValues.push(this.subscriptionsRevenue.contrattiPerc);
    }
    if (this.subscriptionsRevenue.checklistPerc && this.subscriptionsRevenue.checklistPerc > 0) {
      chartValues.push(this.subscriptionsRevenue.checklistPerc);
    }
    // if (this.subscriptionsRevenue.pacchettofissoPerc && this.subscriptionsRevenue.pacchettofissoPerc > 0) {
    //   chartValues.push(this.subscriptionsRevenue.pacchettofissoPerc);
    // }
    if (this.subscriptionsRevenue.nfcAccessPerc && this.subscriptionsRevenue.nfcAccessPerc > 0) {
      chartValues.push(this.subscriptionsRevenue.nfcAccessPerc);
    }
    if (this.subscriptionsRevenue.qrsicurezzaPerc && this.subscriptionsRevenue.qrsicurezzaPerc > 0) {
      chartValues.push(this.subscriptionsRevenue.qrsicurezzaPerc);
    }
    this.doughnutChartData.push(chartValues);

    if (this.loggedUserRoleType === "admin"
      || this.loggedUserRoleType === "operator") {
      // Line chart
      this.lineChartLabels.push(moment().subtract(11, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(10, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(9, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(8, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(7, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(6, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(5, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(4, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(3, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(2, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(1, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().format("MMM YYYY"));

      this.lineChartData.push({
        label: this.translateService.instant('home.full'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      this.lineChartData.push({
        label: this.translateService.instant('home.free'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      this.lineChartData.push({
        label: this.translateService.instant('home.test'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      this.lineChartData.push({
        label: this.translateService.instant('home.contracts'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      this.lineChartData.push({
        label: this.translateService.instant('home.checklist'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      this.lineChartData.push({
        label: this.translateService.instant('home.fixedPackage'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      // this.lineChartData.push({
      //   label: this.translateService.instant('home.qrFire'),
      //   data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      // });
      this.lineChartData.push({
        label: this.translateService.instant('home.qrSecurity'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#EF5350',
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#EC407A',
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#AB47BC',
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#7E57C2',
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#5C6BC0',
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#42A5F5',
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#29B6F6',
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#26C6DA',
      });
    }

    if (this.loggedUserRoleType === "master"
      || this.loggedUserRoleType === "collaborator"
    ) {
      // Line chart
      this.lineChartLabels.push(moment().subtract(11, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(10, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(9, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(8, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(7, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(6, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(5, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(4, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(3, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(2, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().subtract(1, 'months').format("MMM YYYY"));
      this.lineChartLabels.push(moment().format("MMM YYYY"));

      this.lineChartData.push({
        label: this.translateService.instant('home.missingRequired'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        // pointRadius: 1,
        // pointHoverRadius: 1,
      });
      this.lineChartData.push({
        label: this.translateService.instant('home.mandatoryInserted'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        // pointRadius: 1,
        // pointHoverRadius: 1,
      });
      this.lineChartData.push({
        label: this.translateService.instant('home.generics'),
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        // pointRadius: 1,
        // pointHoverRadius: 1,
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#EF5350',
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#EC407A',
      });
      this.lineChartColors.push({
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderColor: '#AB47BC',
      });
    }
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(a: number | string, b: number | string, isAsc: boolean): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortSubscriptionsData(sort: Sort): any {
    const data = JSON.parse(JSON.stringify(this.data.admins));
    if (!sort.active || sort.direction === '') {
      this.sortedSubscriptionsData = data;
      return;
    }

    this.sortedSubscriptionsData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name': return this.compare(a.name, b.name, isAsc);
        case 'full': return this.compare(a.full, b.full, isAsc);
        case 'free': return this.compare(a.free, b.free, isAsc);
        case 'test': return this.compare(a.test, b.test, isAsc);
        case 'contratti': return this.compare(a.contratti, b.contratti, isAsc);
        case 'checklist': return this.compare(a.checklist, b.checklist, isAsc);
        case 'pacchettofisso': return this.compare(a.pacchettofisso, b.pacchettofisso, isAsc);
        case 'nfcAccess': return this.compare(a.nfcAccess, b.nfcAccess, isAsc);
        case 'qrsicurezza': return this.compare(a.qrsicurezza, b.qrsicurezza, isAsc);
        default: return 0;
      }
    });
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  applySubscriptionsFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.sortedSubscriptionsData = this.data.admins.slice();
    const regex = new RegExp(filterValue.trim().toLowerCase(), "gi");
    this.sortedSubscriptionsData = this.data.admins.filter((el: any) => {
      const ret = (
        el.name && el.name.toString().search(regex) > -1)
        || (el.full && el.full.toString().search(regex) > -1)
        || (el.free && el.free.toString().search(regex) > -1)
        || (el.test && el.test.toString().search(regex) > -1)
        || (el.contratti && el.contratti.toString().search(regex) > -1)
        || (el.checklist && el.checklist.toString().search(regex) > -1)
        || (el.pacchettofisso && el.pacchettofisso.toString().search(regex) > -1)
        || (el.nfcAccess && el.nfcAccess.toString().search(regex) > -1)
        || (el.qrsicurezza && el.qrsicurezza.toString().search(regex) > -1)
      return ret;
    })
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortPricesData(sort: Sort): any {
    const data = JSON.parse(JSON.stringify(this.data.admins));
    if (!sort.active || sort.direction === '') {
      this.sortedPricesData = data;
      return;
    }

    this.sortedPricesData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name': return this.compare(a.name, b.name, isAsc);
        case 'newDigitalDocFirstFile': return this.compare(a.newDigitalDocFirstFile, b.newDigitalDocFirstFile, isAsc);
        case 'newDigitalDocOtherFile': return this.compare(a.newDigitalDocOtherFile, b.newDigitalDocOtherFile, isAsc);
        case 'newPaperDocFirstFile': return this.compare(a.newPaperDocFirstFile, b.newPaperDocFirstFile, isAsc);
        case 'newPaperDocOtherFile': return this.compare(a.newPaperDocOtherFile, b.newPaperDocOtherFile, isAsc);
        case 'scanA0': return this.compare(a.scanA0, b.scanA0, isAsc);
        case 'scanA1': return this.compare(a.scanA1, b.scanA1, isAsc);
        case 'scanA2': return this.compare(a.scanA2, b.scanA2, isAsc);
        case 'scanA3': return this.compare(a.scanA3, b.scanA3, isAsc);
        case 'scanA4': return this.compare(a.scanA4, b.scanA4, isAsc);
        case 'scanNoFormat': return this.compare(a.scanNoFormat, b.scanNoFormat, isAsc);
        default: return 0;
      }
    });
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  applyPricesFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.sortedPricesData = JSON.parse(JSON.stringify(this.data.admins)).slice();
    const regex = new RegExp(filterValue.trim().toLowerCase(), "gi");
    this.sortedPricesData = JSON.parse(JSON.stringify(this.data.admins)).filter((el: any) => {
      const ret = (
        el.name && el.name.toString().search(regex) > -1)
        || (el.newDigitalDocFirstFile && el.newDigitalDocFirstFile.toString().search(regex) > -1)
        || (el.newDigitalDocOtherFile && el.newDigitalDocOtherFile.toString().search(regex) > -1)
        || (el.newPaperDocFirstFile && el.newPaperDocFirstFile.toString().search(regex) > -1)
        || (el.newPaperDocOtherFile && el.newPaperDocOtherFile.toString().search(regex) > -1)
        || (el.scanA0 && el.scanA0.toString().search(regex) > -1)
        || (el.scanA1 && el.scanA1.toString().search(regex) > -1)
        || (el.scanA2 && el.scanA2.toString().search(regex) > -1)
        || (el.scanA3 && el.scanA3.toString().search(regex) > -1)
        || (el.scanA4 && el.scanA4.toString().search(regex) > -1)
        || (el.scanNoFormat && el.scanNoFormat.toString().search(regex) > -1)
      return ret;
    })
  }

  /**
   * Export companies in a file with given format
   *
   * @since 1.0.0
   *
   * @param format The format. Can be 'excel' or 'csv'
   */
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('stats.list.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.statsService.getExport().subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Handle structure detail dialog opening
   *
   * @since 1.0.0
   */
  onStructureDetailDialogOpen(topicKey: string) {
    const width = window.innerWidth < 1280 ? "90vw" : "80vw";
    // const dialogRef =
    this.dialog.open(PropertiesInStructureListDialogComponent, {
      data: { topicKey },
      width,
      maxWidth: width,
    });
  }

  /**
   * open pop up info of CPI
   *
   * @since 1.0.0
   */
  onInfoPopUp(topicKey: string): void {
    const width = "350px";
    const title = this.translateService.instant(`stats.tooltip.${topicKey}.title`);
    const message = this.translateService.instant(`stats.tooltip.${topicKey}.message`);
    if (title.trim() !== '') {
      this.dialog.open(InfoDialogComponent, {
        data: { 
          title,
          message,
        },
        width,
        maxWidth: width,
      });
    }
  }
}
